import React, { useState } from 'react';
import axios from 'axios';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import AuthShell, { AuthNotice } from '../Auth/AuthShell';
import { toast } from 'react-hot-toast';

const PasswordReset = () => {
    const { uid, token } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [serverError, setServerError] = useState('');

    const validatePassword = (password) => {
        const minLength = 14;
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumbers = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        const errors = [];
        if (password.length < minLength) errors.push(`At least ${minLength} characters`);
        if (!hasUpperCase) errors.push('One uppercase letter');
        if (!hasLowerCase) errors.push('One lowercase letter');
        if (!hasNumbers) errors.push('One number');
        if (!hasSpecialChar) errors.push('One special character');

        return errors;
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        // Clear any prior server-side error as soon as the user starts editing again
        if (serverError) {
            setServerError('');
        }
    };

    const normalizeErrorMessage = (raw) => {
        if (!raw) return 'Failed to reset password. Please try again.';

        // If backend ever sends an array, use the first element
        if (Array.isArray(raw)) {
            raw = raw[0];
        }

        if (typeof raw !== 'string') {
            return 'Failed to reset password. Please try again.';
        }

        // Sometimes errors come back stringified like ["message"]
        const trimmed = raw.trim();
        if ((trimmed.startsWith('[') && trimmed.endsWith(']')) ||
            (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'string') {
                    return parsed[0];
                }
            } catch {
                // Fallback: strip outer brackets/quotes
                return trimmed.replace(/^\["?/, '').replace(/"?]$/, '');
            }
        }

        return raw;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const { password, confirmPassword } = formData;
        const passwordErrors = validatePassword(password);

        if (passwordErrors.length > 0) {
            toast.error(
                <div>
                    <p>Password must contain:</p>
                    <ul>
                        {passwordErrors.map((error, index) => (
                            <li key={index}>• {error}</li>
                        ))}
                    </ul>
                </div>
            );
            setLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            setLoading(false);
            return;
        }

        try {
            const apiUrl = import.meta.env.VITE_API_URL;
            const response = await axios.post(
                `${apiUrl}/api/password_reset_confirm/`,
                { uidb64: uid, token, password },
                { withCredentials: true }
            );

            toast.success('Password reset successful!');
            // Keep button disabled after success
            setTimeout(() => navigate('/login'), 2000);
        } catch (error) {
            console.error('Error:', error);
            let errorMessage = 'Failed to reset password. Please try again.';
            
            // Handle different error response formats
            if (error.response?.data) {
                // Handle the specific error structure for password reuse
                if (error.response.data.error?.new_password2) {
                    errorMessage = normalizeErrorMessage(error.response.data.error.new_password2);
                }
                // Handle other error formats
                else if (error.response.data.error) {
                    const firstErrorKey = Object.keys(error.response.data.error)[0];
                    if (firstErrorKey && Array.isArray(error.response.data.error[firstErrorKey])) {
                        errorMessage = normalizeErrorMessage(error.response.data.error[firstErrorKey]);
                    } else if (typeof error.response.data.error === 'string') {
                        errorMessage = normalizeErrorMessage(error.response.data.error);
                    }
                }
                // Handle simple message format
                else if (error.response.data.message) {
                    errorMessage = normalizeErrorMessage(error.response.data.message);
                }
            }

            // Show a concise toast and put the detailed message near the field
            setServerError(errorMessage);
            toast.error('Unable to reset password. Please review the message above the form.');
            setLoading(false);
        }
    };

    return (
        <AuthShell
            title="Reset Your Password"
            subtitle="Please enter your new password below"
            footer={
                <>
                    Remember your password?{' '}
                    <Link to="/login" className="link">
                        Sign in
                    </Link>
                </>
            }
        >
            <form className="space-y-5" onSubmit={handleSubmit}>
                <div>
                    <label htmlFor="password" className="label">
                        New Password
                    </label>
                    <div className="relative">
                        <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            id="password"
                            required
                            autoComplete="new-password"
                            className="input h-11 pr-11"
                            value={formData.password}
                            onChange={handleChange}
                        />
                        <button
                            type="button"
                            className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                <div>
                    <label htmlFor="confirmPassword" className="label">
                        Confirm New Password
                    </label>
                    <input
                        type={showPassword ? "text" : "password"}
                        name="confirmPassword"
                        id="confirmPassword"
                        required
                        autoComplete="new-password"
                        className="input h-11"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                    />
                </div>

                {serverError && <AuthNotice tone="danger">{serverError}</AuthNotice>}

                <button type="submit" disabled={loading} className="btn btn-primary btn-lg w-full">
                    {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                    {loading ? 'Resetting...' : 'Reset Password'}
                </button>
            </form>
        </AuthShell>
    );
};

export default PasswordReset;
